import {
  ref, computed, onMounted, onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import axios from 'axios';

// Party, friends and friend code for the profile sidebar: shared by the classic
// ProfileSidebar and the v5 one.
export default function useProfileSidebar(props) {
  const store = useStore();
  const { t } = useI18n();

  const view = ref('main');
  const friendships = ref([]);
  const friendRequests = ref([]);
  const trialCodeCount = ref(0);
  const friendCodeInput = ref('');
  const addingFriend = ref(false);
  const friendMessage = ref(null);
  const friendMessageIsError = ref(false);
  const codeCopied = ref(false);
  const invitedUuids = ref([]);
  const friendPendingRemoval = ref(null);
  const removingFriend = ref(false);
  let refreshTimer = null;
  let copiedTimer = null;

  const canUseMatchmaking = computed(() => !!props.user?.can_use_matchmaking);

  const party = computed(() => store.getters.matchmakingParty);
  const partyMembers = computed(() => party.value?.members || []);
  const isPartyOwner = computed(() => party.value?.owner_id === props.user.id);

  const acceptedFriends = computed(() => friendships.value.filter((f) => f.status === 'accepted'));

  // Live overrides pushed over PresenceChannel win over the flags fetched with the list
  const friendPresence = computed(() => store.getters.friendPresence);
  function isOnline(userLike) {
    const override = friendPresence.value[userLike.uuid];
    return override !== undefined ? override : !!userLike.online;
  }

  const onlineFriends = computed(() => acceptedFriends.value.filter((f) => isOnline(f.friend)));

  const userLink = computed(() => props.user.username || props.user.uuid);

  function isInParty(friend) {
    return partyMembers.value.some((member) => member.uuid === friend.uuid);
  }

  function canRemoveMember(member) {
    // leave (owners can't leave their own party)
    if (member.id === props.user.id) return !isPartyOwner.value;
    return isPartyOwner.value; // kick
  }

  function fetchFriends() {
    axios.get('/friendships').then(({ data }) => { friendships.value = Array.isArray(data) ? data : []; }).catch(() => {});
    axios.get('/friendships/requests').then(({ data }) => {
      friendRequests.value = Array.isArray(data) ? data : [];
    }).catch(() => {});
  }

  function fetchTrialCodes() {
    axios.get('/trial_codes').then(({ data }) => {
      trialCodeCount.value = Array.isArray(data) ? data.length : 0;
    }).catch(() => {});
  }

  function addFriend() {
    addingFriend.value = true;
    friendMessage.value = null;
    axios
      .post('/friendships', { friend_code: friendCodeInput.value.trim() })
      .then(() => {
        friendMessage.value = t('layout.profile_sidebar.friend_request_sent');
        friendMessageIsError.value = false;
        friendCodeInput.value = '';
        fetchFriends();
      })
      .catch((err) => {
        friendMessage.value = err.response?.data?.error || t('layout.profile_sidebar.friend_request_failed');
        friendMessageIsError.value = true;
      })
      .finally(() => { addingFriend.value = false; });
  }

  function acceptRequest(request) {
    axios.post(`/friendships/${request.id}/accept`).then(fetchFriends).catch(() => {});
  }

  function removeFriendship(id) {
    return axios.delete(`/friendships/${id}`).then(fetchFriends).catch(() => {});
  }

  function confirmRemoveFriend() {
    if (!friendPendingRemoval.value || removingFriend.value) return;
    removingFriend.value = true;
    removeFriendship(friendPendingRemoval.value.id).finally(() => {
      removingFriend.value = false;
      friendPendingRemoval.value = null;
    });
  }

  function inviteToParty(friend) {
    if (!party.value?.id) return;
    axios
      .post(`/matchmaking/parties/${party.value.id}/party_invites`, { invitee_uuid: friend.uuid })
      .then(() => invitedUuids.value.push(friend.uuid))
      .catch(() => {});
  }

  function removePartyMember(member) {
    if (!member.member_id) return;
    axios.delete(`/matchmaking/party_members/${member.member_id}`).catch(() => {});
  }

  function copyFriendCode() {
    if (!props.user.friend_code) return;
    navigator.clipboard?.writeText(props.user.friend_code);
    codeCopied.value = true;
    clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => { codeCopied.value = false; }, 1500);
  }

  onMounted(() => {
    fetchFriends();
    fetchTrialCodes();
    refreshTimer = setInterval(fetchFriends, 60000);
  });

  onUnmounted(() => {
    clearInterval(refreshTimer);
    clearTimeout(copiedTimer);
  });

  return {
    view,
    friendRequests,
    trialCodeCount,
    friendCodeInput,
    addingFriend,
    friendMessage,
    friendMessageIsError,
    codeCopied,
    invitedUuids,
    friendPendingRemoval,
    removingFriend,
    canUseMatchmaking,
    partyMembers,
    acceptedFriends,
    isOnline,
    onlineFriends,
    userLink,
    isInParty,
    canRemoveMember,
    addFriend,
    acceptRequest,
    removeFriendship,
    confirmRemoveFriend,
    inviteToParty,
    removePartyMember,
    copyFriendCode,
  };
}
